import { createSelector } from '@reduxjs/toolkit';
import canvases from 'domains/canvases';
import products from 'domains/products';
import dimensions from 'domains/stage_dimensions';
import rooms from 'domains/wall_preview_rooms';
import { calculateArrangementHeight, calculateArrangementWidth } from 'utils/arrangement_util';
import { orientationAndRatioPercentage } from 'utils/math_util';

export const selectDefaultPPI = createSelector(
  [
    canvases.selectors.selectAll,
    products.selectors.selectEntities,
    dimensions.selectors.screenDimensions,
    dimensions.selectors.wallPreviewDimensions,
    rooms.selectors.whichWallPreview,
  ],
  (allCanvases, productsById, screenDimensions, wallPreviewDimensions, whichWallPreview) => {
    // Find the largest configured canvas
    let largestProduct = { width: 0, height: 0 };
    allCanvases.forEach((canvas) => {
      if (!canvas.product) return;

      const product = productsById[canvas.product];
      // Might not have the products yet
      if (!product) return;

      let height;
      let width;
      if (products.utils.isCustomSizeProduct(product)) {
        if (!canvas.width || !canvas.height || !product.custom_options) return;

        ({ width, height } = products.utils.buildCombinedCustomSize(
          canvas.width,
          canvas.height,
          product.custom_options
        ));
      } else {
        const { rotation } = canvas;
        const printSizeX = parseFloat(product.print_size_x);
        const printSizeY = parseFloat(product.print_size_y);
        height = rotation === 90 ? printSizeX : printSizeY;
        width = rotation === 90 ? printSizeY : printSizeX;
      }

      if (width > largestProduct.width) {
        largestProduct = { width, height };
      }
    });

    // Make sure we have all the data to make the calculation
    if (largestProduct.width === 0 || !screenDimensions.smallest) return 0;

    // Get the wall preview sizing dependencies
    const wallPreviewPx = wallPreviewDimensions ? wallPreviewDimensions.width : 0;
    const wallPreviewIn = whichWallPreview?.wallSize;
    const shouldCalculateWallPreview = wallPreviewPx && wallPreviewIn;

    // Determine how many pixels the stage represents
    let width;
    if (shouldCalculateWallPreview) {
      // Multiply the stage width in pixels the ratio of product width in inches by the wall width in inches
      width = wallPreviewPx * (largestProduct.width / wallPreviewIn);
    } else {
      const { ratioPercentage: productRatio, orientation: productOrientation } =
        orientationAndRatioPercentage(largestProduct.width, largestProduct.height);

      // This defaults to filling the smallest dimension and maintaining that ppi
      // It could be expanded if we want to do better about having the product fill
      // as much available space as possible
      width =
        productOrientation === 'landscape'
          ? screenDimensions.smallest
          : screenDimensions.smallest * productRatio;
    }

    return width / largestProduct.width;
  }
);

export const selectZoomedPPI = createSelector(
  [selectDefaultPPI, dimensions.selectors.zoom],
  (defaultPPI, zoom) => {
    return defaultPPI * zoom;
  }
);

export const selectArrangementWidth = createSelector(
  [
    canvases.selectors.selectAll,
    canvases.selectors.selectEntities,
    products.selectors.selectEntities,
    selectZoomedPPI,
  ],
  calculateArrangementWidth
);

export const selectArrangementHeight = createSelector(
  [
    canvases.selectors.selectAll,
    canvases.selectors.selectEntities,
    products.selectors.selectEntities,
    selectZoomedPPI,
  ],
  calculateArrangementHeight
);
